// StateFrame — one update's ordered (quad,value) state entries + the per-frame hash.
// game/stateframe.{h,cpp}. The ~67 AddValue call sites across the sim feed this; entry
// ORDER is part of the determinism contract (GetHash mixes += and ^= non-commutatively).
//
// Faithful-idiomatic deviations (value-identical): the C++ Entry malloc buffer with
// kcEntriesInit/Grow chunking is a JS array reused across Init() calls; capacity management
// affects no hashed value.

import { u32 } from '../math/int32.ts';

export interface Entry {
  quad: number; // dword
  value: number; // dword
}

export class StateFrame {
  private entries_: Entry[] = [];
  private index_ = 0;
  private cUpdates_ = 0;
  private block_ = false;
  private tracker_: unknown;

  // game/stateframe.cpp:11-14
  constructor(tracker: unknown) {
    this.tracker_ = tracker;
  }

  // game/stateframe.cpp:20-32
  Init(cUpdates: number, block: boolean): boolean {
    this.index_ = 0;
    this.cUpdates_ = cUpdates;
    this.block_ = block;
    return true;
  }

  block(): boolean {
    return this.block_;
  } // stateframe.h:30
  updates(): number {
    return this.cUpdates_;
  } // stateframe.h:62

  // game/stateframe.h:32-45
  AddValue(quad: number, value: number, iCounter: number): void {
    this.entries_[this.index_] = { quad: u32(quad), value: u32(value) };
    this.index_++;
    if (iCounter >= 0 && iCounter < this.index_) {
      const e = this.entries_[iCounter];
      e.value = u32(e.value + 1); // entries_[iCounter].value++ (dword wrap)
    }
  }

  // game/stateframe.h:47-58
  AddCountedValue(quad: number): number {
    this.entries_[this.index_] = { quad: u32(quad), value: 0 };
    this.index_++;
    return this.index_ - 1;
  }

  // game/stateframe.cpp:47-56
  GetHash(): number {
    let h = 0;
    for (let i = 0; i < this.index_; i++) {
      h = u32(h + this.entries_[i].quad); // h += entry->quad (dword wrap)
      h = u32(h ^ this.entries_[i].value); // h ^= entry->value
    }
    return h;
  }

  // game/stateframe.cpp:58-67 — FourCC to string, MSB first; ' ' (or NUL) terminates
  // (the C++ writes 0 into the char buffer for trailing spaces, cutting the C string).
  private QuadToString(quad: number): string {
    let s = String.fromCharCode((quad >>> 24) & 0xff);
    for (const shift of [16, 8, 0]) {
      const ch = (quad >>> shift) & 0xff;
      if (ch === 0x20 || ch === 0) break;
      s += String.fromCharCode(ch);
    }
    return s;
  }

  // game/stateframe.cpp:69-113 — counted-group dict JSON
  // {"updates":"N","hash":"%08x","dict":{"PARENT":{"CHILD":"%x",...},...}}
  ToJson(): string {
    let out = '{';
    out += `"updates":"${this.cUpdates_}"`;
    out += `,"hash":"${this.GetHash().toString(16).padStart(8, '0')}"`;
    out += ',"dict":{';

    let first = true;
    for (let i = 0; i < this.index_;) {
      const parent = this.entries_[i];
      const count = parent.value;

      if (!first) out += ',';
      first = false;

      out += `"${this.QuadToString(parent.quad)}":{`;

      i = i + 1;
      const start = i;

      let firstchild = true;
      for (; i < start + count && i < this.index_; i++) {
        if (!firstchild) out += ',';
        firstchild = false;
        const entry = this.entries_[i];
        out += `"${this.QuadToString(entry.quad)}":"${entry.value.toString(16)}"`;
      }

      out += '}';
    }
    out += '}}';
    return out;
  }
}
